import React, { useEffect } from "react";
import { StickToBottom, useStickToBottomContext } from "use-stick-to-bottom";

import { faArrowDown } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import ChatErrorBanner from "./ChatErrorBanner";
import ChatMessage from "./ChatMessage";

import { useAskPerformYardStore } from "../AskPerformYardStore";

/** Registers the scrollToBottom function in the store so other components can use it. */
const ScrollToBottomSync: React.FC = () => {
	const { scrollToBottom } = useStickToBottomContext();
	const setScrollToBottom = useAskPerformYardStore((state) => state.setScrollToBottom);

	useEffect(() => {
		setScrollToBottom(() => scrollToBottom());
		return () => setScrollToBottom(null);
	}, [scrollToBottom, setScrollToBottom]);

	return null;
};

/** Scroll-to-bottom button that appears when user has scrolled up. */
const ScrollToBottomButton: React.FC<{ hasError: boolean }> = ({ hasError }) => {
	const { isAtBottom, scrollToBottom } = useStickToBottomContext();

	if (isAtBottom) {
		return null;
	}

	return (
		<button
			type="button"
			onClick={() => scrollToBottom()}
			className={`absolute left-1/2 z-10 flex h-8 w-8 -translate-x-1/2 cursor-pointer items-center justify-center rounded-full border border-neutral-700 bg-white transition-all hover:bg-neutral-50 ${hasError ? "bottom-14" : "bottom-4"}`}
			aria-label="Scroll to bottom"
		>
			<FontAwesomeIcon icon={faArrowDown} className="h-3 w-3 text-neutral-700" />
		</button>
	);
};

export const ChatMessages: React.FC = () => {
	const messages = useAskPerformYardStore((state) => state.messages);
	const error = useAskPerformYardStore((state) => state.error);

	const hasError = !!error;

	return (
		<StickToBottom className="relative flex-1 overflow-hidden" resize="smooth" initial="smooth">
			<StickToBottom.Content
				scrollClassName="![scrollbar-gutter:auto]"
				className={`flex flex-col items-start gap-2 p-2 pl-3 pr-4 ${hasError ? "pb-12" : ""}`}
			>
				{messages.map((message) => (
					<ChatMessage key={message.id} message={message} />
				))}
			</StickToBottom.Content>

			{error && (
				<div className="absolute bottom-0 left-0 right-0 z-10 px-4 pb-2">
					<ChatErrorBanner type="error" message={error} />
				</div>
			)}

			<ScrollToBottomSync />
			<ScrollToBottomButton hasError={hasError} />
		</StickToBottom>
	);
};

export default ChatMessages;
